import { useCallback, useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { ReportResultsModal, type ReportResultsModalPayload } from './ReportResultsModal';
import { usePinnedReports } from './hooks/usePins';
import { useReportBuilderPreview, type ReportBuilderQuery } from './hooks/useReportBuilder';
import {
  useRunLegacyReport,
  type SavedReport,
  type GenerateReportParams,
} from './hooks/useReports';
import { exportBuilderResultToExcel } from './reportResultsUtils';

type ResultsModalState = {
  open: boolean;
  title: string;
  loading: boolean;
  error: string | null;
  payload: ReportResultsModalPayload | null;
  legacyParams: GenerateReportParams | null;
  reportId?: string;
};

const initialModalState: ResultsModalState = {
  open: false,
  title: '',
  loading: false,
  error: null,
  payload: null,
  legacyParams: null,
};

/**
 * MyReportsCard — dashboard card listing the current user's pinned reports.
 * Clicking a report runs it in place and shows the results in a modal, reusing
 * the same execution paths (builder vs legacy) and results modal as the Reports
 * page. Gated by the caller (dashboard renders it only with reporting.read).
 *
 * canExport controls whether the "Export Excel" action is offered in the modal.
 */
export function MyReportsCard({ canExport = false }: { canExport?: boolean }) {
  const { data: pinnedReports, isLoading } = usePinnedReports();
  const previewMutation = useReportBuilderPreview();
  const legacyRunMutation = useRunLegacyReport();
  const [modal, setModal] = useState<ResultsModalState>(initialModalState);

  const closeModal = useCallback(() => setModal(initialModalState), []);

  const runCustomQuery = useCallback(
    async (title: string, query: ReportBuilderQuery, reportId?: string) => {
      setModal({
        open: true,
        title,
        reportId,
        loading: true,
        error: null,
        payload: null,
        legacyParams: null,
      });
      try {
        const result = await previewMutation.mutateAsync(query);
        setModal({
          open: true,
          title,
          reportId,
          loading: false,
          error: null,
          payload: { kind: 'builder', result },
          legacyParams: null,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to run report';
        setModal((m) => ({ ...m, loading: false, error: message }));
      }
    },
    [previewMutation],
  );

  const runLegacyReport = useCallback(
    async (saved: SavedReport, page = 1) => {
      const params: GenerateReportParams = {
        reportType: saved.reportType,
        filters: saved.filters,
        columns: saved.columns?.map((c) => c.id),
        page,
        pageSize: 50,
      };
      setModal({
        open: true,
        title: saved.name,
        reportId: saved.id,
        loading: true,
        error: null,
        payload: null,
        legacyParams: params,
      });
      try {
        const result = await legacyRunMutation.mutateAsync(params);
        setModal({
          open: true,
          title: saved.name,
          reportId: saved.id,
          loading: false,
          error: null,
          payload: { kind: 'legacy', result, page },
          legacyParams: params,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to run report';
        setModal((m) => ({ ...m, loading: false, error: message }));
      }
    },
    [legacyRunMutation],
  );

  const handleRun = useCallback(
    (saved: SavedReport) => {
      if (saved.reportType === 'custom' && saved.query) {
        void runCustomQuery(saved.name, saved.query as unknown as ReportBuilderQuery, saved.id);
      } else if (saved.reportType !== 'custom') {
        void runLegacyReport(saved, 1);
      }
    },
    [runCustomQuery, runLegacyReport],
  );

  const handleLegacyPageChange = useCallback(
    (page: number) => {
      if (!modal.reportId || !pinnedReports) return;
      const saved = pinnedReports.find((r) => r.id === modal.reportId);
      if (!saved) return;
      void runLegacyReport(saved, page);
    },
    [modal.reportId, pinnedReports, runLegacyReport],
  );

  const handleExport = useCallback(() => {
    if (modal.payload?.kind === 'builder') {
      exportBuilderResultToExcel(modal.payload.result, modal.title);
    }
  }, [modal]);

  return (
    <Card>
      <CardHeader
        title="My Reports"
        description="Reports you pinned from the Reports page — click to run"
      />
      <CardContent>
        {isLoading ? (
          <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
            Loading pinned reports…
          </p>
        ) : !pinnedReports || pinnedReports.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
            No pinned reports yet. Pin a report from the Reports page to run it here.
          </p>
        ) : (
          <ul className="divide-y divide-border dark:divide-border-dark" role="list">
            {pinnedReports.map((report) => (
              <li key={report.id}>
                <button
                  type="button"
                  onClick={() => handleRun(report)}
                  className="flex w-full items-center justify-between gap-3 px-1 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {report.name}
                    </span>
                    {report.ownerName && (
                      <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                        {report.ownerName}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs font-medium text-brand-600 dark:text-brand-400">
                    Run →
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <ReportResultsModal
        open={modal.open}
        title={modal.title}
        loading={modal.loading}
        error={modal.error}
        payload={modal.payload}
        canExport={canExport}
        canEdit={false}
        onClose={closeModal}
        onExport={handleExport}
        onLegacyPageChange={handleLegacyPageChange}
      />
    </Card>
  );
}
